import { Component, Element, Fragment, h, Listen, Method, Prop, State, Watch } from '@stencil/core';
import { loadStripe } from '@stripe/stripe-js';
import {
  centsToFixed2,
  createSlotDivOnDocumentLevel,
  currentUrlWithExtraQueryParams,
  dateToLocalizedString,
  loadScript,
} from '../../utils/utils';
import {
  addError,
  changeRecurringPaymentMode,
  closeProcessingSpinner,
  completeBooking,
  completeWithStripeSepa,
  confirmPayWithStripeCC,
  initForOrder,
  initPayment,
  payPalPlusInit,
  payPalPlusUpdate,
  payWithPayPal,
  payWithPrePayment,
  payWithStripeBancontact,
  payWithStripeBlik,
  payWithStripeCC,
  payWithStripeExpressCheckout,
  payWithStripeIdeal,
  payWithStripeP24,
  payWithStripeSepa,
  payWithStripeSofort,
  payWithStripeTwint,
  payWithVoucher,
  reserve,
  selectPaymentMethodOption,
  selectPaymentSplitOption,
} from '../../redux/actions';
import { Unsubscribe } from 'redux';
import { store } from '@stencil/redux';
import { GiftCard, PaymentConfig, PaymentMethod, RtrError, SplitOption } from '../../redux/types';
import { formatPrice, t } from '../../redux/utils';
import { ComponentView, Price, SelectOption, SplitOptionType } from '../../types/types';
import { CodeStatus, PaymentCustomer, PaymentOption, ValidateAndPayProps } from './utils/types';
import { pay, SuccessParams } from './utils/pay';
import { validatePayment } from './utils/validate';
import { ErrorType } from '../rtr-errors/utils';
import { RootState } from '../../redux/reducers';

@Component({
  tag: 'rtr-payment-engine',
  shadow: true,
})
export class RtrPaymentEngine {
  @Prop() orderReference: string;
  @Prop() view: ComponentView;
  @Prop() paymentConfig: PaymentConfig;
  @Prop() giftCard: GiftCard;
  @Prop() selectedSplitOption: SplitOption;
  @Prop() paymentMethod: PaymentMethod;
  @Prop() secondaryPaymentMethod: PaymentMethod;
  @Prop() depositPrice: Price;
  @Prop() paymentCustomer: PaymentCustomer;
  @Prop() country: string;
  @Prop() billingStepValues: any;
  @Prop() amountToPay!: number;
  @Prop() prepay: CodeStatus;
  @Prop() errors: RtrError[] = [];
  @Prop() paymentFailed: boolean;
  @Prop() isOrder: boolean;
  @Prop() setParentWaiting: (value: boolean) => void;
  @Prop() setMethodValidated: (value: boolean) => void;
  @Prop({ mutable: true }) isRecurringMode: boolean;
  @Prop({ mutable: true }) currencyCode: string;
  @Prop({ mutable: true }) customer: any;


  @Prop({ mutable: true }) stripeIbanRef: any;
  @Prop({ mutable: true }) stripeCcRef: any;

  @State() payPalPlusPaymentId: any;
  @State() sepaName: any;
  @State() blikCode: any;
  @State() showPaymentOptions: boolean = true;
  @State() isModeDecided: boolean = false;


  // validation state
  @State() validationOptionsError: boolean = false;
  @State() validationMethodError: boolean = false;
  @State() canValidateMethod: boolean;

  // values for validation
  @State() validatedCCNumber: boolean;
  @State() validatedCCExpiry: boolean;
  @State() validatedCCCvc: boolean;
  @State() validatedSepaIban: boolean;
  @State() validatedSepaName: boolean;
  @State() validatedBlik: boolean;
  @State() validatedGiftCard: boolean;
  @State() showExpressCheckout: boolean = false;
  @State() showPayPalCheckout: boolean = false;

  @Element() private paymentEl: HTMLElement;
  stripe: any;

  initPayment!: typeof initPayment;
  initForOrder!: typeof initForOrder;
  selectPaymentSplitOption!: typeof selectPaymentSplitOption;
  payWithPrePayment!: typeof payWithPrePayment;
  reserve!: typeof reserve;
  payWithStripeCC!: typeof payWithStripeCC;
  confirmPayWithStripeCC!: typeof confirmPayWithStripeCC;
  payWithStripeBancontact!: typeof payWithStripeBancontact;
  payWithStripeSofort!: typeof payWithStripeSofort;
  payWithPayPal!: typeof payWithPayPal;
  payPalPlusUpdate!: typeof payPalPlusUpdate;
  payWithStripeIdeal!: typeof payWithStripeIdeal;
  payWithStripeSepa!: typeof payWithStripeSepa;
  payWithStripeP24!: typeof payWithStripeP24;
  payWithStripeBlik!: typeof payWithStripeBlik;
  payWithStripeTwint!: typeof payWithStripeTwint;
  payWithStripeExpressCheckout!: typeof payWithStripeExpressCheckout;

  payPalPlusInit!: typeof payPalPlusInit;
  changeRecurringPaymentMode!: typeof changeRecurringPaymentMode;
  selectPaymentMethodOption!: typeof selectPaymentMethodOption;
  completeWithStripeSepa!: typeof completeWithStripeSepa;
  payWithVoucher!: typeof payWithVoucher;
  addError!: typeof addError;
  closeProcessingSpinner!: typeof closeProcessingSpinner;
  completeBooking!: typeof completeBooking;
  unsubscribe!: Unsubscribe;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        isRecurringMode: state.app.recurringPaymentMode,
        currencyCode: state.system.currencyCode,
        customer: state.app.customer,
      };
    });

    store.mapDispatchToProps(this, {
      initPayment,
      initForOrder,
      payWithStripeCC,
      changeRecurringPaymentMode,
      selectPaymentSplitOption,
      confirmPayWithStripeCC,
      payWithStripeBancontact,
      payWithStripeSofort,
      payWithPayPal,
      payPalPlusUpdate,
      payWithStripeIdeal,
      payWithStripeSepa,
      payWithStripeP24,
      payWithStripeBlik,
      payWithStripeTwint,
      payWithStripeExpressCheckout,
      payPalPlusInit,
      selectPaymentMethodOption,
      completeWithStripeSepa,
      payWithPrePayment,
      reserve,
      payWithVoucher,
      addError,
      closeProcessingSpinner,
      completeBooking: completeBooking,
    });
  }

  componentDidLoad() {
    if (this.paymentFailed) {
      this.addError({ message: t('error.' + ErrorType.PaymentFailed) });
    }

    loadScript('https://www.paypalobjects.com/webstatic/ppplus/ppplus.min.js', undefined);
    if (this.paymentConfig.paypalClientId) {
      loadScript(`https://www.paypal.com/sdk/js?client-id=${this.paymentConfig.paypalClientId}&currency=${this.currencyCode}&components=buttons`, undefined);
    }
    createSlotDivOnDocumentLevel(this.paymentEl, 'stripe-sepa-iban', 'iban-element');
    createSlotDivOnDocumentLevel(this.paymentEl, 'stripe-cc-cardNumber', 'stripe-cc-cardNumber-element');
    createSlotDivOnDocumentLevel(this.paymentEl, 'stripe-cc-cardExpiry', 'stripe-cc-cardExpiry-element');
    createSlotDivOnDocumentLevel(this.paymentEl, 'stripe-cc-cardCvc', 'stripe-cc-cardCvc-element');
    createSlotDivOnDocumentLevel(this.paymentEl, 'stripe-express-checkout', 'stripe-express-checkout-element');
    createSlotDivOnDocumentLevel(this.paymentEl, 'paypal-checkout', 'paypal-checkout-element');
    loadStripe(this.paymentConfig.stripeApiKey).then(stripe => {
      this.stripe = stripe;

      const fieldStyle = {
        style: {
          base: {
            'color': '#46435B',
            'height': '40px',
            'border': '1px solid #F0F0F2',
            'fontSize': '14px',
            'fontWeigh': '400',
            'fontFamily': 'Nunito',
            'padding': '0',
            'margin': '0',
            '::placeholder': {
              color: '#7E7C8D',
            },
          },
          invalid: {
            iconColor: '#FF0066',
            color: '#FF0066',
          },
        },
      };

      const elements = this.stripe.elements();
      const cardNumberEl = elements.create('cardNumber', { showIcon: true, ...fieldStyle });
      const cardExpiryEl = elements.create('cardExpiry', fieldStyle);
      const cardCvcEl = elements.create('cardCvc', fieldStyle);
      cardNumberEl.mount('#stripe-cc-cardNumber-element');
      cardExpiryEl.mount('#stripe-cc-cardExpiry-element');
      cardCvcEl.mount('#stripe-cc-cardCvc-element');
      cardNumberEl.on('change', e => {
        this.validatedCCNumber = !e.empty && !e.error;
        this.validationMethodError = this.canValidateMethod && !this.isCCValid;
        this.setMethodValidated?.(this.isCCValid);
      });
      cardExpiryEl.on('change', e => {
        this.validatedCCExpiry = !e.empty && !e.error;
        this.validationMethodError = this.canValidateMethod && !this.isCCValid;
        this.setMethodValidated?.(this.isCCValid);
      });
      cardCvcEl.on('change', e => {
        this.validatedCCCvc = !e.empty && !e.error;
        this.validationMethodError = this.canValidateMethod && !this.isCCValid;
        this.setMethodValidated?.(this.isCCValid);
      });

      this.stripeCcRef = cardNumberEl;

      const options = {
        ...fieldStyle,
        supportedCountries: ['SEPA'],
        // Elements can use a placeholder as an example IBAN that reflects
        // the IBAN format of your customer's country. If you know your
        // customer's country, we recommend that you pass it to the Element as the
        // placeholderCountry.
        placeholderCountry: 'DE',
      };

      // Create an instance of the IBAN Element
      const ibanEl = elements.create('iban', options);

      // Add an instance of the IBAN Element into the `iban-element` <div>
      ibanEl.mount('#iban-element');
      ibanEl.on('change', e => {
        this.validatedSepaIban = !e.empty && !e.error;
        this.validateSepa();
      });
      this.stripeIbanRef = ibanEl;
    });

    this.changePaymentType(this.paymentConfig?.methodsRecurring?.length > 0);
  }

  componentDidUpdate() {
    if (!this.paymentMethod?.value && this.giftCard) {
      this.changePaymentMethod(this.giftCardOption);
    } else if (this.paymentMethod?.value === 'giftcard' && this.giftCard) {
      this.showPaymentOptions = false;
    }
  }

  @Watch('paymentFailed')
  handlePaymentFailed() {
    if (this.paymentFailed) {
      this.addError({ message: t('error.' + ErrorType.PaymentFailed) });
    }
  }

  @Watch('giftCard')
  giftCardChanged() {
    if (this.paymentMethod?.value === PaymentOption.GiftCard) {
      this.validatedGiftCard = this.isGiftCardValid;
      this.validationMethodError = this.canValidateMethod && !this.isGiftCardValid;
      this.setMethodValidated?.(this.isGiftCardValid);
    }
  }

  @Listen('rtrPaymentOptionsNotValidated', { capture: true, target: 'body' })
  async paymentOptionsNotValidated() {
    this.validationOptionsError = true;
  }

  @Listen('rtrPaymentMethodsNotValidated', { capture: true, target: 'body' })
  async scrollToError() {
    this.canValidateMethod = true;
    this.validationMethodError = true;
  }

  @Method()
  async validateMethod() {
    if (this.paymentMethod?.value === PaymentOption.StripeCC || this.secondaryPaymentMethod?.value === PaymentOption.StripeCC) {
      return this.isCCValid;
    }

    return true;
  }

  get isCCValid(): boolean {
    return this.validatedCCNumber && this.validatedCCExpiry && this.validatedCCCvc;
  }

  get isGiftCardValid(): boolean {
    return !!this.giftCard;
  }

  get isBlikValid(): boolean {
    if (isNaN(this.blikCode) || this.blikCode.length !== 6) {
      return false;
    }

    return true;
  }

  handleStripeExpressCheckout() {
    this.showExpressCheckout = true;
  }

  handlePayPalCheckout() {
    this.showPayPalCheckout = true;
  }

  handleConfirmStripeExpressCheckout = (_: string, elements: any) => {
    this.payWithStripeExpressCheckout(
      this.orderReference,
      this.isOrder,
      'express-checkout-stripe',
      (intentClientSecret) => {
        window.onbeforeunload = undefined;
        this.stripe.confirmPayment({
          elements,
          clientSecret: intentClientSecret,
          confirmParams: {
            return_url: currentUrlWithExtraQueryParams([
              { name: 'rentware-payment', value: 'stripe-source' },
              { name: 'rentware-order-id', value: this.orderReference },
            ]),
          },
        });
      },
      () => {
        console.log('error!');
        this.addError({
          type: ErrorType.PaymentProblemWithMethod,
          message: t('error.' + ErrorType.PaymentFailed),
        });
        this.showExpressCheckout = false;
        this.closeProcessingSpinner();
      },
    );
  };

  handlePaypalCheckoutCreateOrder = (): Promise<string> => {
    return new Promise(async (resolve, reject) => {
      this.payWithPayPal(this.orderReference, 'paypal-checkout', this.isOrder, (data: any) => resolve(data.id), () => reject());
    });
  };

  handlePaypalCheckoutApproveOrder = (paymentId: string): Promise<void> => {
    return new Promise(async (resolve) => {
      resolve();

      window.onbeforeunload = undefined;
      window.location.href = currentUrlWithExtraQueryParams([
        { name: 'rentware-payment', value: 'paypal-success' },
        { name: 'rentware-order-id', value: this.orderReference },
        { name: 'token', value: paymentId },
      ]);
    });
  };

  /**********************************/
  /*** This action starts payment ***/

  /**********************************/
  @Listen('rtrStartPaymentEvent', { capture: true, target: 'body' })
  async validateAndPay(eventProps: { detail: ValidateAndPayProps }) {
    const paymentProps = eventProps.detail;
    const isOrder = paymentProps.isOrder === true || paymentProps.isOrder === false ? paymentProps.isOrder : this.isOrder;
    const validated: boolean = validatePayment({
      method: this.paymentMethod?.value,
      component: this.view,
      giftCard: this.giftCard,
      secondaryMethod: this.secondaryPaymentMethod?.value,
      amountToPay: this.amountToPay,
    });

    if (!validated) {
      paymentProps?.onValidateError?.();
      return false;
    } else {
      paymentProps?.onValidateSuccess?.();
    }

    const handlePaymentSuccess = (params?: SuccessParams) => {
      if (isOrder) {
        finishPayment(params);
      } else {
        finishPaymentNoOrder(params);
      }
      this.closeProcessingSpinner();
    };

    const finishPayment = (params?: SuccessParams) => {
      if (!!params?.url) {
        paymentProps?.onPaymentSuccess?.();
        window.location.href = params.url;
      } else if (params?.goPayPal) {
        paymentProps?.onPaymentSuccess?.();
        (window as any).PAYPAL.apps.PPP.doCheckout();
      } else {
        if (!!paymentProps?.onPaymentSuccessNoRedirect) {
          paymentProps?.onPaymentSuccessNoRedirect();
        } else {
          paymentProps?.onPaymentSuccess?.();
        }
      }
    };

    const finishPaymentNoOrder = (params?: SuccessParams) => {
      if (!!params?.url) {
        window.location.href = params.url;
      } else if (params?.goPayPal) {
        (window as any).PAYPAL.apps.PPP.doCheckout();
      } else {
        if (!!paymentProps?.onPaymentSuccessNoRedirect) {
          paymentProps?.onPaymentSuccessNoRedirect();
        } else {
          paymentProps?.onPaymentSuccess?.();
        }
      }
    };

    const finishWithError = (errMsg?: string) => {
      this.addError({
        type: ErrorType.PaymentProblemWithMethod,
        message: errMsg || t('error.' + ErrorType.PaymentFailed),
      });
      paymentProps?.onPaymentError?.();
      this.closeProcessingSpinner();
    };

    const selectedMethodValue = this.getSelectedPaymentMethodValue();
    if (selectedMethodValue === PaymentOption.StripeExpressCheckout) {
      this.closeProcessingSpinner();

      return this.handleStripeExpressCheckout();
    }

    if (selectedMethodValue === PaymentOption.PayPalCheckout) {
      this.closeProcessingSpinner();

      return this.handlePayPalCheckout();
    }

    await pay({
      isOrder,
      selectedMethod: selectedMethodValue,
      orderReference: this.orderReference,
      amountToPay: this.amountToPay,
      customer: this.paymentCustomer,
      stripe: this.stripe,
      stripeCcRef: this.stripeCcRef,
      stripeIbanRef: this.stripeIbanRef,
      sepaName: this.sepaName,
      blikCode: this.blikCode,
      payPalPlusPaymentId: this.payPalPlusPaymentId,
      payPrepayment: this.payWithPrePayment,
      payWithStripeCC: this.payWithStripeCC,
      payWithStripeSofort: this.payWithStripeSofort,
      payWithStripeIdeal: this.payWithStripeIdeal,
      payWithStripeSepa: this.payWithStripeSepa,
      payWithStripeBancontact: this.payWithStripeBancontact,
      payWithStripeP24: this.payWithStripeP24,
      payWithStripeBlik: this.payWithStripeBlik,
      payWithStripeTwint: this.payWithStripeTwint,
      payWithPayPal: this.payWithPayPal,
      payPalPlusUpdate: this.payPalPlusUpdate,
      reserve: this.reserve,
      payWithVoucher: this.payWithVoucher,
      confirmPayWithStripeCC: this.confirmPayWithStripeCC,
      completeWithStripeSepa: this.completeWithStripeSepa,
      onSuccess: handlePaymentSuccess,
      onError: finishWithError,
      prepay: this.prepay,
      onBlikSuccess: this.completeBooking,
    });
  }

  getSelectedPaymentMethodValue(): string {
    return this.paymentMethod?.value === PaymentOption.GiftCard ? this.secondaryPaymentMethod?.value : this.paymentMethod?.value;
  }

  initPayPalPlus = () => {
    createSlotDivOnDocumentLevel(this.paymentEl, 'paypal-plus', 'ppplus', '300px');

    this.payPalPlusInit(
      this.orderReference,
      data => {
        this.onPayPalPlusPaymentIdChange(data.paymentId);
        (window as any).PAYPAL.apps.PPP({
          approvalUrl: data.approvalUrl,
          placeholder: 'ppplus',
          mode: data.mode,
          country: this.country,
          language: 'de_DE',
          buttonLocation: 'outside',
          showLoadingIndicator: true,
        });
      },
      undefined,
    );
  };

  onPayPalPlusPaymentIdChange = name => {
    this.payPalPlusPaymentId = name;
  };

  onSepaNameChange = name => {
    this.validatedSepaName = name && name.length > 0;
    this.sepaName = name;
    this.validateSepa();
  };

  isSepaValid = () => {
    return this.validatedSepaIban && this.validatedSepaName;
  };

  validateSepa = () => {
    this.validationMethodError = this.canValidateMethod && !this.isSepaValid();
    this.setMethodValidated?.(this.isSepaValid());
  };

  handleBlikCodeChange = (code: string) => {
    this.blikCode = code;
    this.validatedBlik = this.isBlikValid;
    this.validationMethodError = this.canValidateMethod && !this.isBlikValid;
    this.setMethodValidated?.(this.isBlikValid);
  };

  handleShowChangeMethod = () => {
    this.showPaymentOptions = true;
    this.validationOptionsError = false;
    this.selectPaymentMethodOption(null);
  };

  handleSecondaryOptionChange = (item: SelectOption) => {
    this.handleOptionChange(item, true);
  };

  handleOptionChange = (item: SelectOption, isSecondary?: boolean) => {
    this.validationOptionsError = false;
    this.validationMethodError = false;
    this.canValidateMethod = false;

    if (item?.value === PaymentOption.StripeCC) {
      this.setMethodValidated?.(this.isCCValid);
    } else if (item?.value === PaymentOption.StripeSepa) {
      this.setMethodValidated?.(this.isSepaValid());
    } else if (item?.value === PaymentOption.StripeBlik) {
      this.blikCode = null;
      this.setMethodValidated?.(false);
    } else if (item?.value === PaymentOption.GiftCard) {
      this.setMethodValidated?.(this.isGiftCardValid);
    } else {
      this.setMethodValidated?.(true); // other method do not need to be validated
    }

    this.changePaymentMethod(item, isSecondary, this.isRecurringMode);
  };

  changePaymentType(isRecurring: boolean) {
    this.changeRecurringPaymentMode(isRecurring);
    this.handleShowChangeMethod();

    this.isModeDecided = true;
    if (isRecurring) {
      const option: SplitOption = {
        excludeDeposit: false,
        extraCartItemId: '',
        type: SplitOptionType.Amount,
        value: parseFloat(centsToFixed2(this.paymentConfig.prePaymentRecurring)),
      };

      this.setWaiting();

      this.selectPaymentSplitOption({
        orderReference: this.orderReference,
        option: option,
        onSuccess: () => {
          this.initPayment({
            orderReference: this.orderReference,
            onSuccess: () => {
              this.initForOrder(this.releaseWaiting, this.orderReference);
            },
            onError: this.releaseWaiting,
          });
        },
        onError: this.releaseWaiting,
      });
    }
  }

  changePaymentMethod(method: PaymentMethod, isSecondary?: boolean, isRecurring?: boolean) {
    this.selectPaymentMethodOption(method, isSecondary, isRecurring);
    this.showPaymentOptions = false;
    if (method.value === 'paypal-plus') {
      this.initPayPalPlus();
    }
  }

  setWaiting = () => {
    this.setParentWaiting?.(true);
  };

  releaseWaiting = () => {
    this.setParentWaiting?.(false);
  };

  hasPaymentMethod = (method: string): boolean => {
    const isOrderView = this.view === ComponentView.Order;
    const methods = this.isRecurringMode && !isOrderView ? this.paymentConfig.methodsRecurring : this.paymentConfig.methods;

    return methods.some(item => item === method);
  };

  get giftCardOption() {
    return { label: t('payment.giftCardLabel'), value: 'giftcard' };
  }

  get allowChange() {
    return !this.giftCard;
  }

  get canShowOptions(): boolean {
    return !!this.amountToPay || this.paymentMethod?.value === 'reservation' || this.paymentMethod?.value === 'giftcard' || !this.paymentMethod?.value;
  }

  get paymentOptions(): SelectOption[] {
    const options = [];
    if (this.hasPaymentMethod('credit-card-stripe')) {
      options.push({
        label: t('payment.creditCardLabel'),
        value: PaymentOption.StripeCC,
      });
    }
    if (this.hasPaymentMethod('prepayment')) {
      options.push({
        label: t('payment.prepaymentNote'),
        value: PaymentOption.Prepayment,
      });
    }
    if (this.hasPaymentMethod('reservation')) {
      options.push({
        label: t('payment.reservationNote'),
        value: PaymentOption.Reservation,
      });
    }
    if (this.hasPaymentMethod('paypal')) {
      options.push({ label: 'PayPal', value: PaymentOption.PayPal });
    }
    if (this.hasPaymentMethod('paypal-plus')) {
      options.push({ label: 'PayPal', value: PaymentOption.PayPalPlus });
    }
    if (this.hasPaymentMethod('paypal-checkout')) {
      options.push({ label: 'PP Checkout', value: PaymentOption.PayPalCheckout });
    }
    if (this.hasPaymentMethod('sofort-stripe')) {
      options.push({ label: 'Sofort', value: PaymentOption.StripeSofort });
    }
    if (this.hasPaymentMethod('ideal-stripe')) {
      options.push({ label: 'Ideal', value: PaymentOption.StripeIdeal });
    }
    if (this.hasPaymentMethod('sepa-direct-debit-stripe')) {
      options.push({ label: 'Sepa', value: PaymentOption.StripeSepa });
    }
    if (this.hasPaymentMethod('bancontact-stripe')) {
      options.push({ label: 'Bancontact', value: PaymentOption.StripeBanContact });
    }
    if (this.hasPaymentMethod('p24-stripe')) {
      options.push({ label: 'Przelewy24', value: PaymentOption.StripeP24 });
    }
    if (this.hasPaymentMethod('blik-stripe')) {
      options.push({ label: 'Blik', value: PaymentOption.StripeBlik });
    }
    if (this.hasPaymentMethod('twint-stripe')) {
      options.push({ label: 'TWINT', value: PaymentOption.StripeTwint });
    }
    if (this.hasPaymentMethod('express-checkout-stripe')) {
      options.push({ label: 'Express Checkout', value: PaymentOption.StripeExpressCheckout });
    }
    if (this.hasPaymentMethod('gift-card')) {
      options.push(this.giftCardOption);
    }

    // recurring
    if (this.hasPaymentMethod('credit-card-stripe-recurring')) {
      options.push({
        label: t('payment.creditCardLabel'),
        value: PaymentOption.StripeCC,
      });
    }
    if (this.hasPaymentMethod('sepa-direct-debit-stripe-recurring')) {
      options.push({
        label: 'Sepa',
        value: PaymentOption.StripeSepa,
      });
    }

    return options;
  }

  render() {
    if (this.view === ComponentView.Order) {
      return (
        <Fragment>
          <rtr-payment-options-order
            allowChange={this.allowChange}
            paymentOptions={this.paymentOptions}
            currentMethod={this.paymentMethod}
            onOptionChange={this.handleOptionChange}
            optionsError={this.validationOptionsError}
            onShowChangeMethod={this.handleShowChangeMethod}
            showPaymentOptions={this.showPaymentOptions}
          />

          <rtr-payment-method-order
            currentMethod={this.paymentMethod}
            onBlikCodeChange={this.handleBlikCodeChange}
            onSepaNameChange={this.onSepaNameChange}
            errors={this.errors}
            showPaymentOptions={this.showPaymentOptions}
          />
        </Fragment>
      );
    }

    const hasRecurringMethods = this.paymentConfig?.methodsRecurring?.length > 0;

    return (
      <Fragment>
        {this.showExpressCheckout &&
          <rtr-stripe-express-checkout
            amountToPay={this.amountToPay}
            stripe={this.stripe}
            method={this.getSelectedPaymentMethodValue()}
            currency={this.currencyCode.toLowerCase()}
            onClose={() => this.showExpressCheckout = false}
            onConfirm={this.handleConfirmStripeExpressCheckout}
            style={{ minHeight: '400px' }}
          />
        }
        {this.showPayPalCheckout &&
          <rtr-paypal-checkout
            amountToPay={this.amountToPay}
            onClose={() => this.showPayPalCheckout = false}
            onCreateOrder={this.handlePaypalCheckoutCreateOrder}
            onApproveOrder={this.handlePaypalCheckoutApproveOrder}
            style={{ minHeight: '400px' }}
          />
        }
        {this.canShowOptions && hasRecurringMethods && (
          <div>
            <rentware-radio
              name='paymentType'
              label={t('payment.typeRecurring')}
              value='recurring'
              checked={this.isRecurringMode}
              onChange={(e) => {
                e.preventDefault();
                this.changePaymentType(true);
              }}
            />
            <rentware-radio
              name='paymentType'
              label={t('payment.typeOneTime')}
              value='one-time'
              checked={!this.isRecurringMode}
              onChange={(e) => {
                e.preventDefault();
                this.changePaymentType(false);
              }}
            />
            <rentware-spacer size='L' />
          </div>
        )}
        {this.canShowOptions && (
          <Fragment>
            <rtr-payment-options
              allowChange={this.allowChange}
              paymentOptions={this.paymentOptions}
              currentMethod={this.paymentMethod}
              hasSecondaryPayment={!!this.giftCard}
              onOptionChange={this.handleOptionChange}
              validationOptionsError={this.validationOptionsError}
              onShowChangeMethod={this.handleShowChangeMethod}
              showPaymentOptions={this.showPaymentOptions}
            />

            <rtr-payment-method
              orderReference={this.orderReference}
              paymentOptions={this.paymentOptions}
              currentMethod={this.paymentMethod}
              secondaryPaymentMethod={this.secondaryPaymentMethod}
              onSepaNameChange={this.onSepaNameChange}
              onBlikCodeChange={this.handleBlikCodeChange}
              giftCard={this.giftCard}
              errors={this.errors}
              validationOptionsError={this.validationOptionsError}
              validationMethodError={this.validationMethodError}
              showPaymentOptions={this.showPaymentOptions}
              prepaymentPasscodeEnabled={this.paymentConfig.prepaymentPasscodeEnabled}
              onSecondaryOptionChange={this.handleSecondaryOptionChange}
            />

            <rentware-spacer size='L' />
          </Fragment>
        )}

        {this.isModeDecided && !this.isRecurringMode &&
          <rtr-payment-split
            key={this.paymentMethod?.value}
            orderReference={this.orderReference}
            selectedMethod={this.paymentMethod?.value}
            paymentConfig={this.paymentConfig}
            selectedSplitOption={this.selectedSplitOption}
            depositPrice={this.depositPrice}
            setWaiting={this.setWaiting}
            releaseWaiting={this.releaseWaiting}
          />
        }

        {this.isModeDecided && this.isRecurringMode &&
          <div>
            <strong>{t('payment.recurringScheduleTitle')}</strong>
            <rentware-spacer size='S' />
            <ul>
              <li>{t('payment.recurringPayNow')} - {formatPrice(this.amountToPay)}</li>
              {this.paymentConfig.duePayments.map((item) => {
                return (
                  <li
                    style={{ display: 'block' }}>{dateToLocalizedString(new Date(item.date))} - {formatPrice(item.amount)} ({t('payment.recurringPaymentDaysOfUsage', { days: item.daysOfUsage })})</li>
                );
              })}
            </ul>
          </div>
        }
      </Fragment>
    );
  }
}
